import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

import type { PhysicalFitnessAssessment } from "~/api/physicalFitnessAssessments";
import { CORE_MUSCLE_ENDURANCE_OPTION } from "~/api/physicalFitnessAssessments";
import { Crunches, Plank, SitUp } from "~/icons";
import { calculateTime } from "~/utils";
import { PerformanceDetails } from ".";

interface CoreMuscleEnduranceSectionProps {
  lastPFA: PhysicalFitnessAssessment;
}

export const CoreMuscleEnduranceSection = ({
  lastPFA,
}: CoreMuscleEnduranceSectionProps) => {
  const { t } = useTranslation();

  const option = lastPFA.core_muscle_endurance_option;

  const qualified = lastPFA.core_muscle_endurance_score != 0;

  const totalScore = lastPFA.core_muscle_endurance_score / 10;

  return (
    <PerformanceDetails.Section>
      <PerformanceDetails.Subtitle className="pb-4">
        {t("pfas.core_muscle")}
      </PerformanceDetails.Subtitle>
      <PerformanceDetails.Card>
        <PerformanceDetails.CardHeader>
          <PerformanceDetails.CardTitle>
            {option === CORE_MUSCLE_ENDURANCE_OPTION.ONE_MIN && (
              <SitUp
                className={twMerge(
                  qualified
                    ? "text-primary-40 dark:text-primary-80"
                    : "text-warning-95",
                )}
              />
            )}
            {option ===
              CORE_MUSCLE_ENDURANCE_OPTION.TWO_MIN_CROSS_LEG_REVERSE_CRUNCH && (
              <Crunches
                className={twMerge(
                  qualified
                    ? "text-primary-40 dark:text-primary-80"
                    : "text-warning-95",
                )}
              />
            )}
            {option ===
              CORE_MUSCLE_ENDURANCE_OPTION.FOREARM_PLANK_IN_SECONDS && (
              <Plank
                className={twMerge(
                  qualified
                    ? "text-primary-40 dark:text-primary-80"
                    : "text-warning-95",
                )}
              />
            )}
            <PerformanceDetails.Subtitle
              className={twMerge(
                qualified
                  ? "text-primary-40 dark:text-primary-80"
                  : "text-warning-95",
              )}
            >
              {t(`pfas.${lastPFA.core_muscle_endurance_option}`)}
            </PerformanceDetails.Subtitle>
          </PerformanceDetails.CardTitle>
          <PerformanceDetails.QualifiedTag
            className={twMerge(
              qualified
                ? "bg-info-50 dark:bg-info-30"
                : "bg-warning-80 text-black",
            )}
          >
            {qualified ? t("pfas.qualified") : t("pfas.not_qualified")}
          </PerformanceDetails.QualifiedTag>
        </PerformanceDetails.CardHeader>
        <PerformanceDetails.CardBody>
          <PerformanceDetails.CardResult>
            <PerformanceDetails.CardResultValue className="">
              {option === CORE_MUSCLE_ENDURANCE_OPTION.FOREARM_PLANK_IN_SECONDS
                ? t("pfas.time")
                : t("pfas.repetitions")}
            </PerformanceDetails.CardResultValue>
            <PerformanceDetails.CardResultValue className="pt-2">
              {option === CORE_MUSCLE_ENDURANCE_OPTION.FOREARM_PLANK_IN_SECONDS
                ? calculateTime(lastPFA.core_muscle_endurance_value)
                : t("pfas.reps", { reps: lastPFA.core_muscle_endurance_value })}
            </PerformanceDetails.CardResultValue>
          </PerformanceDetails.CardResult>
          <PerformanceDetails.CardResult>
            <PerformanceDetails.CardResultValue className="">
              {t("pfas.score")}
            </PerformanceDetails.CardResultValue>
            <PerformanceDetails.CardResultValue className="pt-2">
              {t("pfas.over_20_points", { totalScore: totalScore })}
            </PerformanceDetails.CardResultValue>
          </PerformanceDetails.CardResult>
        </PerformanceDetails.CardBody>
      </PerformanceDetails.Card>
    </PerformanceDetails.Section>
  );
};
